/** @format */

import { Component, OnInit, inject, input } from '@angular/core';
import {
	PersonalizationData,
	PersonalizationState,
	ProductStyle,
	StyleDescriptiveAttribute
} from '@shared/data-access/interfaces';
import { PersonalizedLineAidNumberString, PersonalizedNumberRange } from '@pdp/data-access/enums';
import { UntypedFormControl, Validators } from '@angular/forms';
import { selectErrorList, selectSelectedTextColorAttribute } from '@pdp/attributes/data-access';
import {
	validateBannedWord,
	validateNumber,
	validateNumberRequired,
	validateRequired,
	validateSpecialChar
} from '@pdp/custom/utils';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { FileService, Logger, UtilityService } from '@shared/data-access/services';

import { HmfSelectedOption } from '@dsg-tech/homefield-angular';

import { PersonalizedLineAidTextString } from '@shared/data-access/enums';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { debounceTime } from 'rxjs/operators';
import { pdpJsonFileName } from '@pdp/data-access/constants';
import { personalizationOptionsUpdated } from '@pdp/data-access/state';

@Component({
	selector: 'pdp-attributes-components-personalization',
	templateUrl: './personalization.component.html',
	styleUrl: './personalization.component.scss',
	standalone: false
})
export class PersonalizationComponent implements OnInit {
	/*
	 * Input / Output
	 */
	readonly product = input.required<ProductStyle>();

	private readonly fileService = inject(FileService);
	private readonly log = inject(Logger);
	private readonly utilityService = inject(UtilityService);
	private readonly store = inject(Store);

	/*
	 * Public
	 */
	public bannedWordsList: string[];
	public hasPersonalizedError = false;
	public hasPersonalizedLineAid: boolean;
	public hasPersonalizedNumber: boolean;
	public hasPersonalizedNumberError = false;
	public hasPersonalizedNumberRange = false;
	public hasPersonalizedText: boolean;
	public hasPersonalizedTextError = false;
	public personalizedData: PersonalizationData = {};
	public personalizedLineAidText: string;
	public personalizedNumberCharLimit: number;
	public personalizedNumberLine = new UntypedFormControl('', Validators.required);
	public personalizedNumberLineCharsRemaining: number;
	public personalizedNumberLineRange: PersonalizedNumberRange;
	public personalizedTextCase: string;
	public personalizedTextCharLimit: number;
	public personalizedTextCurrentCharLimit: number;
	public personalizedTextLine1 = new UntypedFormControl('', [
		validateSpecialChar,
		validateRequired
	]);
	public personalizedTextLine1CharsRemaining: number;
	public personalizedTextLine2 = new UntypedFormControl({ value: '', disabled: true }, [
		validateSpecialChar
	]);
	public personalizedTextLine2CharsRemaining: number;
	public personalizedTextLine3 = new UntypedFormControl({ value: '', disabled: true }, [
		validateSpecialChar
	]);
	public personalizedTextLine3CharsRemaining: number;
	public selectedLineAid = '1';

	/**
	 * @description
	 * subscription to get the selected text color from state
	 */
	public selectSelectedTextColorAttribute$ = this.store.select(selectSelectedTextColorAttribute);

	/**
	 * @description
	 * object that gets emitted to the store for other components to subscribe to
	 */
	public personalizationEvent: PersonalizationState = {
		exists: true,
		valid: false,
		personalization: {}
	};

	/*
	 * Public Readonly
	 */
	public readonly personalizedNumberCharLimitDefault = 2;
	public readonly personalizedTextCharLimitDefault = 17;
	public readonly personalizedLineAidCharLimitDefault = 3;

	/*
	 * Private
	 */
	private debounceSubject: Subject<number> = new Subject();

	/**
	 * @description
	 * ngLifeCycle method for component init
	 */
	ngOnInit() {
		// debounce so we don't execute on every keypress
		this.debounceSubject.pipe(debounceTime(1000)).subscribe((searchTextValue) => {
			this.checkPersonalizedLine(searchTextValue);
		});

		// Set flags for type of personalized product
		this.hasPersonalizedText = this.utilityService.isTruthy(this.product().isPersonalizedText);
		this.hasPersonalizedLineAid = this.utilityService.isTruthy(
			this.product().isPersonalizedLineAid
		);
		this.hasPersonalizedNumber = this.product().personalizedText.includes('NUMBER');

		// text line length
		this.personalizedTextCharLimit =
			this.product().personalizedCharLimit || this.personalizedTextCharLimitDefault;
		this.personalizedTextCurrentCharLimit = this.personalizedTextCharLimit;
		this.personalizedTextLine1CharsRemaining = this.personalizedTextCurrentCharLimit;
		this.personalizedTextLine2CharsRemaining = this.personalizedTextCurrentCharLimit;
		this.personalizedTextLine3CharsRemaining = this.personalizedTextCurrentCharLimit;

		// number line range
		this.personalizedNumberLineRange = this.getPersonalizedNumberRange(
			this.product().descriptiveAttributes
		);

		// number line length
		this.personalizedNumberCharLimit =
			this.product().emPersonalizedDigitsRequired || this.personalizedNumberCharLimitDefault;
		this.personalizedNumberLineCharsRemaining = this.personalizedNumberCharLimit;

		this.personalizedNumberLine = new UntypedFormControl('', [
			validateNumberRequired(this.personalizedNumberCharLimit),
			validateNumber(this.personalizedNumberCharLimit, this.personalizedNumberLineRange)
		]);

		// personalized text case
		this.personalizedTextCase = this.product().personalizedTextCase?.toLowerCase() || 'mixed';
		this.personalizedData.x_personalizedTextCase = this.personalizedTextCase;

		// Load banned words
		this.fileService
			.getDataFileByType$(pdpJsonFileName.bannedWords, 'json')
			.subscribe((data) => {
				this.bannedWordsList = data.BannedWords;
				this.personalizedTextLine1 = new UntypedFormControl('', [
					validateSpecialChar,
					validateBannedWord(this.bannedWordsList),
					validateRequired
				]);
				this.personalizedTextLine2 = new UntypedFormControl({ value: '', disabled: true }, [
					validateSpecialChar,
					validateBannedWord(this.bannedWordsList)
				]);
				this.personalizedTextLine3 = new UntypedFormControl({ value: '', disabled: true }, [
					validateSpecialChar,
					validateBannedWord(this.bannedWordsList)
				]);
			});

		// personalized product
		this.personalizedData.x_personalizedAttr = true;

		// personalized text or line aid product
		if (this.hasPersonalizedText || this.hasPersonalizedLineAid) {
			this.personalizedData.x_personalizationTextLine1 = undefined;
			this.personalizedData.x_personalizationTextLine2 = undefined;
			this.personalizedData.x_personalizationTextLine3 = undefined;
		}

		// personalized line aid product
		if (this.hasPersonalizedLineAid) {
			this.personalizedLineAidText = PersonalizedLineAidTextString.NONE;
			this.personalizedData.x_personalizationLineStyle = PersonalizedLineAidTextString.NONE;
		}

		// personalized number product
		if (this.hasPersonalizedNumber) {
			this.personalizedData.x_personalizationNumberLine1 = undefined;
		}

		// Default Event personalization data
		this.personalizationEvent.personalization = this.personalizedData;

		// Default valid to false
		this.personalizationEvent.valid = false;

		this.checkForProductSetupErrors();

		this.store.select(selectErrorList).subscribe((errors) => {
			this.handlePersonalizationErrors(errors);
		});
	}

	private checkForProductSetupErrors() {
		// Debug messages for product setup issues
		if (
			(this.hasPersonalizedText &&
				typeof this.product().personalizedCharLimit === 'undefined') ||
			(this.hasPersonalizedLineAid &&
				typeof this.product().personalizedCharLimit === 'undefined') ||
			(this.hasPersonalizedNumber &&
				typeof this.product().emPersonalizedDigitsRequired === 'undefined')
		) {
			// Set the error details
			const errorDetails = {
				eCode: `${this.product().partNumber}`,
				personalizeType: `${this.hasPersonalizedNumber ? 'number' : 'text'}`
			};

			// Log the message
			this.log.warn(
				`${
					DCSG_CONSTANTS.errorHandling.messagePrefixes.global.generic
				}Missing max char attribute for personalized product "${
					this.product().partNumber
				}": ${this.hasPersonalizedNumber ? 'number' : 'text'}`,
				errorDetails
			);
		}
	}

	private handlePersonalizationErrors(errors: Record<string, string>) {
		if (errors?.['Personalization']) {
			// Check if product has text/LINEAID
			if (this.hasPersonalizedText || this.hasPersonalizedLineAid) {
				// material error wont fire if the form field isn't dirty
				this.personalizedTextLine1.markAsDirty();

				// Check the text lines
				this.checkPersonalizedLine(1);
				this.checkPersonalizedLine(2);
				this.checkPersonalizedLine(3);
			}

			// Check if product has number
			if (this.hasPersonalizedNumber) {
				// material error wont fire if the form field isn't dirty
				this.personalizedNumberLine.markAsDirty();

				// Check the number line
				this.checkPersonalizedLine();
			}
		}
	}

	public debounceCheckPersonalizedLine(lineNumber: number) {
		this.debounceSubject.next(lineNumber);
	}

	private setPersonalizedTextCase(text: string) {
		// Check the text case value: LOWER, UPPER, MIXED (NOTE: Don't need to do anything for MIXED)
		if (
			typeof this.personalizedTextCase === 'string' &&
			this.personalizedTextCase.toLowerCase() === 'lower'
		) {
			return `${text}`.toLowerCase();
		}
		if (
			typeof this.personalizedTextCase === 'string' &&
			this.personalizedTextCase.toLowerCase() === 'upper'
		) {
			return `${text}`.toUpperCase();
		}
		return text;
	}

	/**
	 * @description
	 * Checks personalized lines for errors, resets fields if a previous optional
	 * field is cleared (e.g. clears line 3 if line 2 is cleared), converts lines
	 * to proper case if necessary, sets personalization Event data, sets global state
	 */
	public checkPersonalizedLine(lineNumber?: number) {
		// Output event common
		this.personalizationEvent.personalization.hasLineAid = this.hasPersonalizedLineAid;
		this.personalizationEvent.personalization.hasTextLine =
			this.hasPersonalizedText || this.hasPersonalizedLineAid;
		this.personalizationEvent.personalization.hasNumberLine = this.hasPersonalizedNumber;

		/*
		 * Check to see which line types are present
		 */
		// Text
		if (this.hasPersonalizedText || this.hasPersonalizedLineAid) {
			// Check for errors
			this.hasPersonalizedTextError = !this.areTextLinesValid();

			if (typeof lineNumber !== 'undefined') {
				// Text line property string
				const personalizedTextLineString = `personalizedTextLine${lineNumber}`;

				// Text line property
				const personalizedTextLine = this[personalizedTextLineString];

				// Text line chars remaining string
				const personalizedTextLineCharsRemainingString = `personalizedTextLine${lineNumber}CharsRemaining`;

				// personalized text validation
				if (typeof personalizedTextLine.value !== 'undefined') {
					// Get the line text length
					const textLength = `${personalizedTextLine.value}`.length;

					// Set the line text remaining length
					this[personalizedTextLineCharsRemainingString] =
						this.personalizedTextCurrentCharLimit - textLength;

					// Reset input boxes if the previous one is empty and enable input boxes if the previous one has text
					this.adjustInputFields(lineNumber, textLength);

					// setting personalization object that will be set in state
					this.personalizationEvent.personalization[
						`x_personalizationTextLine${lineNumber}`
					] = this.setPersonalizedTextCase(personalizedTextLine.value);
				}
			}
		}

		// Number
		if (this.hasPersonalizedNumber) {
			this.personalizedNumberLineCharsRemaining =
				this.personalizedNumberCharLimit - this.personalizedNumberLine.value.length;
			this.personalizationEvent.personalization.x_personalizationNumberLine1 =
				this.personalizedNumberLine.value;
			this.personalizationEvent.personalization.numberLineCharLimit =
				this.personalizedNumberCharLimit;

			// Check for errors
			this.hasPersonalizedNumberError = !this.areNumberLinesValid();
		}

		// Evaluate both error types
		this.hasPersonalizedError =
			this.hasPersonalizedTextError || this.hasPersonalizedNumberError;

		// Set Event error value
		this.personalizationEvent.valid = !this.hasPersonalizedError;

		//create a deep copy so that the instance of object in the component is still malleable
		const selectedPersonalizationOptions = JSON.parse(
			JSON.stringify(this.personalizationEvent)
		);

		this.store.dispatch(personalizationOptionsUpdated({ selectedPersonalizationOptions }));
	}

	/**
	 * @description
	 * Checks to see if text lines are valid
	 */
	public areTextLinesValid(): boolean {
		return !(
			((this.hasPersonalizedText || this.hasPersonalizedLineAid) &&
				this.personalizedTextLine1.invalid) ||
			this.personalizedTextLine2.invalid ||
			this.personalizedTextLine3.invalid
		);
	}

	/**
	 * @description
	 * Checks to see if number lines are valid
	 */
	public areNumberLinesValid(): boolean {
		return !(this.hasPersonalizedNumber && this.personalizedNumberLine.invalid);
	}

	/**
	 * @description
	 * Accepts a line number and text length and determines if any of the lines
	 * need to be reset or enabled
	 */
	public adjustInputFields(lineNumber: number, textLength: number) {
		if (lineNumber === 1) {
			if (textLength === 0) {
				this.resetTextInput(2);
				this.resetTextInput(3);
			} else {
				this.personalizedTextLine2.enable();
			}
		} else if (lineNumber === 2) {
			if (textLength === 0) {
				this.resetTextInput(3);
			} else {
				this.personalizedTextLine3.enable();
			}
		}
	}

	/**
	 * @description
	 * Check to see if the current product has a number range for validations
	 */
	public getPersonalizedNumberRange(
		descAttributes: StyleDescriptiveAttribute[]
	): PersonalizedNumberRange {
		let numberRangeValue: PersonalizedNumberRange;

		// number line validation restriction
		if (typeof descAttributes === 'object' && descAttributes.length > 0) {
			// Iterate the descriptive attributes
			for (let i = 0, length = descAttributes.length; i < length; i += 1) {
				// Check the current attribute name
				if (
					typeof descAttributes[i].name === 'string' &&
					typeof descAttributes[i].value === 'string' &&
					descAttributes[i].name.toLowerCase() === 'personalized_num_range'
				) {
					// Get the values for parsing
					const attributeValue = `${descAttributes[i].value}`.trim().replace(' ', '');

					// Check the value against the Enum
					if (attributeValue === PersonalizedNumberRange.SINGLE_LOW) {
						numberRangeValue = PersonalizedNumberRange.SINGLE_LOW;
					} else if (attributeValue === PersonalizedNumberRange.SINGLE_ALL) {
						numberRangeValue = PersonalizedNumberRange.SINGLE_ALL;
					} else if (attributeValue === PersonalizedNumberRange.DOUBLE) {
						numberRangeValue = PersonalizedNumberRange.DOUBLE;
					}

					// Break out of the loop
					break;
				}
			}
		}

		// Return the value
		return numberRangeValue;
	}

	/**
	 * @description
	 * Resets input value and personalization data for line and disables the input
	 */
	public resetTextInput(lineNumber: number) {
		// Reset personalization Event object for line number provided
		this.personalizationEvent.personalization[`x_personalizationTextLine${lineNumber}`] = '';

		// Set the line text remaining length
		this[`personalizedTextLine${lineNumber}CharsRemaining`] =
			this.personalizedTextCurrentCharLimit;

		// Get a reference to the line
		const personalizedLine = this[`personalizedTextLine${lineNumber}`];

		// Reset the line
		personalizedLine.setValue('');

		// Disable the line
		personalizedLine.disable();
	}

	public handleHmfSelectableChange(options: HmfSelectedOption[]) {
		const lineAidVal = options[0]?.id;
		if (lineAidVal) {
			this.displayLineAid(String(lineAidVal));
		}
	}

	/**
	 * @description
	 * To determine the text max length in case of LINEAID and which
	 * text fields to display
	 */
	public displayLineAid(lineAidVal: string) {
		// Check the LINEAID value to determine max length of text and clear lines 2 and 3
		if (
			lineAidVal === PersonalizedLineAidNumberString.STRAIGHT ||
			lineAidVal === PersonalizedLineAidNumberString.TARGET
		) {
			// Set to the LINEAID char limit
			this.personalizedTextCurrentCharLimit = this.personalizedLineAidCharLimitDefault;

			// Check to see if something was already entered into line 2
			if (
				typeof this.personalizedTextLine2.value === 'string' &&
				this.personalizedTextLine2.value.trim().length > 0
			) {
				// Reset line 2
				this.personalizedTextLine2.setValue('');
				this.personalizedData.x_personalizationTextLine2 = '';

				// Check line 2
				this.checkPersonalizedLine(2);
			}

			// Check to see if something was already entered into line 3
			if (
				typeof this.personalizedTextLine3.value === 'string' &&
				this.personalizedTextLine3.value.trim().length > 0
			) {
				// Reset line 3
				this.personalizedTextLine3.setValue('');
				this.personalizedData.x_personalizationTextLine3 = '';

				// Check line 3
				this.checkPersonalizedLine(3);
			}
		} else {
			// Reset back to the default/original char limit from product setup
			this.personalizedTextCurrentCharLimit = this.personalizedTextCharLimit;
		}

		// Check to see if something was already entered into line 1
		if (
			typeof this.personalizedTextLine1.value === 'string' &&
			this.personalizedTextLine1.value.trim().length > 0
		) {
			// Check to see if we need to truncate line 1
			if (
				this.personalizedTextLine1.value.trim().length >
				this.personalizedTextCurrentCharLimit
			) {
				// Truncate the text
				this.personalizedTextLine1.setValue(
					this.personalizedTextLine1.value
						.trim()
						.substr(0, this.personalizedTextCurrentCharLimit)
				);

				// Update personalized data
				this.personalizedData.x_personalizationTextLine1 = this.personalizedTextLine1.value;
			}

			// Check line 1
			this.checkPersonalizedLine(1);
		}

		// Update line 1 max length to the updated length
		this.personalizedTextLine1CharsRemaining =
			this.personalizedTextCurrentCharLimit - this.personalizedTextLine1.value.trim().length;

		// Update LINEAID display
		this.updateLineAidText(lineAidVal, this.personalizedData);
	}

	/**
	 * @description
	 * Sets the LINEAID text value
	 */
	public updateLineAidText(lineAidVal: string, personalizedData: PersonalizationData) {
		// Update the currently selected LINEAID value
		this.selectedLineAid = lineAidVal;

		if (lineAidVal === PersonalizedLineAidNumberString.NONE) {
			this.personalizedLineAidText = PersonalizedLineAidTextString.NONE;
		} else if (lineAidVal === PersonalizedLineAidNumberString.STRAIGHT) {
			this.personalizedLineAidText = PersonalizedLineAidTextString.STRAIGHT;
		} else if (lineAidVal === PersonalizedLineAidNumberString.TARGET) {
			this.personalizedLineAidText = PersonalizedLineAidTextString.TARGET;
		}

		personalizedData['x_personalizationLineStyle'] = this.personalizedLineAidText;
	}

	/**
	 * Gets error message for a text line
	 */
	public getTextLineErrorMessage(lineNumber: number): string | null {
		// Get a reference to the line
		const personalizedLine = `personalizedTextLine${lineNumber}`;

		// Check to see if it has an error
		if (this[personalizedLine].hasError('validateSpecialChar')) {
			// Check to see if the line is marked as dirty
			if (this[personalizedLine].dirty) {
				// Mark the line as touched
				this[personalizedLine].markAsTouched();
			}

			// Return the error
			return this[personalizedLine].getError('validateSpecialChar').message;
		}

		// Return null because there is not an error
		return null;
	}

	/**
	 * Gets error message for a number line
	 */
	public getNumberLineErrorMessage(): string | null {
		// Check to see if it has an error
		if (this.personalizedNumberLine.hasError('validateNumber')) {
			// Check to see if the line is marked as dirty
			if (this.personalizedNumberLine.dirty) {
				// Mark the line as touched
				this.personalizedNumberLine.markAsTouched();
			}

			// Return the error
			return this.personalizedNumberLine.getError('validateNumber').message;
		}

		// Return null because there is not an error
		return null;
	}
}
